import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { mapPatientListScopeTabToScope, useScopedPatientsQuery } from '@/entities/care-patients';
import { blurflower } from '@/shared/assets';
import { useDebouncedValue } from '@/shared';

import { DashboardHeader } from './ui/DashboardHeader';
import { PatientQueue, type QueueFilterTab } from './ui/PatientQueue';
import { QueueSummary } from './ui/QueueSummary';

/**
 * DashboardPage — the clinician's home triage hub.
 *
 * Composition-only page that assembles the dashboard layout.
 * Uses the same blurred abstract background as the login page
 * with a liquid glass (glassmorphism) effect on all cards.
 *
 * Data is loaded inline — no per-component skeleton states since
 * the workspace skeleton already covers the initial navigation.
 */
function DashboardPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [queueFilter, setQueueFilter] = useState<QueueFilterTab>('urgent');
  const debouncedSearchQuery = useDebouncedValue(searchQuery, 250);
  const trimmedSearchQuery = debouncedSearchQuery.trim();
  const scope = mapPatientListScopeTabToScope(queueFilter);

  const { patients, isLoading } = useScopedPatientsQuery({ query: trimmedSearchQuery, scope });

  const handleSelectPatient = (userId: string) => {
    navigate(`/patient/${userId}`);
  };

  return (
    <div className="dark relative h-screen overflow-y-auto bg-background text-foreground">
      {/* Background — fixed so it stays visible while scrolling */}
      <img
        src={blurflower}
        alt=""
        aria-hidden="true"
        className="fixed inset-0 h-full w-full object-cover opacity-60"
      />
      {/* Gradient overlay — also fixed */}
      <div
        className="fixed inset-0 bg-gradient-to-b from-background/30 via-background/60 to-background/80"
        aria-hidden="true"
      />

      <div className="relative mx-auto w-full max-w-7xl px-6 py-8 lg:px-8">
        {/* Header — brand, search, controls, and a live subtitle (queue summary). */}
        <DashboardHeader
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          subtitle={<QueueSummary patients={patients} />}
        />

        {/* Main Content: Queue */}
        <section className="mt-6 pb-8">
          <PatientQueue
            patients={patients}
            isPatientsLoading={isLoading}
            activeTab={queueFilter}
            onTabChange={setQueueFilter}
            onSelectPatient={handleSelectPatient}
          />
        </section>
      </div>
    </div>
  );
}

export default DashboardPage;
